import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Loader2, Send, Save, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Pick the matchday squad for a league fixture and share it with parents. */
export const MatchDaySquad = ({ fixtureId, onClose, onSaved }) => {
  const [players, setPlayers] = useState([]);
  const [picked, setPicked] = useState([]);
  const [meetTime, setMeetTime] = useState("");
  const [note, setNote] = useState("");
  const [sharedAt, setSharedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/academy/fixtures/${fixtureId}/squad`, { withCredentials: true });
      setPlayers(r.data.players || []);
      setPicked((r.data.players || []).filter((p) => p.selected).map((p) => p.id));
      setMeetTime(r.data.fixture?.meet_time || r.data.fixture?.start_time || "");
      setNote(r.data.fixture?.squad_note || "");
      setSharedAt(r.data.fixture?.squad_shared_at || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the squad");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [fixtureId]); // eslint-disable-line

  const toggle = (id) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  const pickAvailable = () =>
    setPicked(players.filter((p) => p.attending !== false).map((p) => p.id));

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/academy/fixtures/${fixtureId}/squad`,
        { player_ids: picked, meet_time: meetTime, squad_note: note }, { withCredentials: true });
      toast.success(`Squad saved · ${picked.length} player(s)`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the squad");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/academy/fixtures/${fixtureId}/squad`,
        { player_ids: picked, meet_time: meetTime, squad_note: note }, { withCredentials: true });
      const r = await axios.post(`${API}/academy/fixtures/${fixtureId}/squad/share`, {},
        { withCredentials: true });
      toast.success(`Squad shared with ${r.data.parents_notified} parent(s)`);
      setSharedAt(new Date().toISOString());
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not share the squad");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-slate-700 bg-slate-950/80 p-3" data-testid="matchday-squad-panel">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-sm text-white">
          Match day squad <span className="text-slate-500">{picked.length} picked</span>
        </p>
        <Button size="sm" variant="ghost" className="h-8 text-slate-400" onClick={onClose}
          data-testid="squad-close-btn"><X className="w-4 h-4" /></Button>
      </div>

      {loading ? (
        <p className="text-xs text-slate-400">Loading roster…</p>
      ) : players.length === 0 ? (
        <p className="text-xs text-amber-300" data-testid="squad-no-players">
          No players in this team yet — add them to the league side first.
        </p>
      ) : (
        <>
          <div className="grid gap-1 sm:grid-cols-2 max-h-72 overflow-y-auto">
            {players.map((p) => (
              <label key={p.id}
                className={`flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer border ${
                  picked.includes(p.id) ? "bg-emerald-500/10 border-emerald-500/40" : "bg-slate-900/60 border-slate-800"
                }`}
                data-testid={`squad-player-${p.id}`}>
                <input type="checkbox" checked={picked.includes(p.id)} onChange={() => toggle(p.id)}
                  className="w-4 h-4" data-testid={`squad-check-${p.id}`} />
                <span className="text-xs text-white truncate flex-1">
                  {p.jersey_number ? `#${p.jersey_number} ` : ""}{p.name}
                </span>
                {p.attending === false && (
                  <Badge className="bg-amber-500/20 text-amber-300 text-[10px]">not coming</Badge>
                )}
                {p.attending === true && (
                  <Badge className="bg-emerald-500/20 text-emerald-300 text-[10px]">available</Badge>
                )}
              </label>
            ))}
          </div>

          <div className="grid gap-2 sm:grid-cols-2 mt-3">
            <Input type="time" value={meetTime} onChange={(e) => setMeetTime(e.target.value)}
              className="h-10 bg-slate-900 border-slate-700" placeholder="Meet time"
              data-testid="squad-meet-time" />
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Anything parents should know" className="h-10 bg-slate-900 border-slate-700"
              data-testid="squad-note" />
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            <Button size="sm" variant="outline" className="h-9 text-[11px]" onClick={pickAvailable}
              data-testid="squad-pick-available">Pick everyone available</Button>
            <Button size="sm" className="h-9 text-[11px] bg-slate-700 hover:bg-slate-600"
              onClick={save} disabled={busy} data-testid="squad-save-btn">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Save
            </Button>
            <Button size="sm" className="h-9 text-[11px] bg-emerald-600 hover:bg-emerald-500"
              onClick={share} disabled={busy || picked.length === 0} data-testid="squad-share-btn">
              <Send className="w-3 h-3 mr-1" /> Share with parents
            </Button>
            {sharedAt && (
              <span className="text-[11px] text-slate-500 self-center" data-testid="squad-shared-note">
                shared {new Date(sharedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};
